import { useQuery } from "@tanstack/react-query";
import { getSites, getSiteThemes } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert } from "@/components/ui/alert";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { post } from "@rails/request.js";

const createSite = async (params: any) => {
  const response = await post("/sites.json", {
    body: JSON.stringify({ site: params }),
    responseKind: "json",
  });
  if (!response.ok) {
    const error = await response.json;
    throw new Error(error.errors ? error.errors.join(", ") : "Failed to create site");
  }
  return response.json;
};

const SitesIndex: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: sites, isLoading, error } = useQuery({ queryKey: ["sites"], queryFn: getSites });

  // Fetch public themes using the first site as a workaround
  const firstSiteId = sites && sites.length > 0 ? sites[0].id : null;
  const {
    data: themesData,
    isLoading: isThemesLoading,
    error: themesError,
  } = useQuery({
    queryKey: ["public_themes", firstSiteId],
    queryFn: () => firstSiteId ? getSiteThemes(firstSiteId) : Promise.resolve(null),
    enabled: !!firstSiteId,
  });
  const publicThemes = themesData?.public_themes || [];

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    subdomain: "",
    custom_domain: "",
    description: "",
    keywords: "",
  });

  const mutation = useMutation({
    mutationFn: createSite,
    onSuccess: () => {
      setOpen(false);
      setForm({
        name: "",
        subdomain: "",
        custom_domain: "",
        description: "",
        keywords: "",
      });
      queryClient.invalidateQueries({ queryKey: ["sites"] });
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate(form);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">Sites</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="default">New Site</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Site configuration</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="ie:. site name"
                  required
                />
              </div>
              <div>
                <Label htmlFor="subdomain">Subdomain</Label>
                <Input
                  id="subdomain"
                  name="subdomain"
                  value={form.subdomain}
                  onChange={handleChange}
                  placeholder='ie:. "misite"'
                  required
                />
              </div>
              <div>
                <Label htmlFor="custom_domain">Custom domain</Label>
                <Input
                  id="custom_domain"
                  name="custom_domain"
                  value={form.custom_domain}
                  onChange={handleChange}
                  placeholder='ie:. "misite.com" without www. or any subdomain'
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  placeholder="Tell a brief description of your site"
                />
              </div>
              <div>
                <Label htmlFor="keywords">Keywords</Label>
                <Input
                  id="keywords"
                  name="keywords"
                  value={form.keywords}
                  onChange={handleChange}
                  placeholder="keywords like art, music, smoking (comma separated)"
                />
              </div>
              {mutation.isError && (
                <Alert variant="destructive" className="mb-2">
                  {(mutation.error as Error).message}
                </Alert>
              )}
              <DialogFooter>
                <Button type="submit" disabled={mutation.isPending}>
                  {mutation.isPending ? "Creating..." : "Create Site"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      
      {isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="p-6">
              <Skeleton className="w-3/5 h-6 mb-3" />
              <Skeleton className="w-2/5 h-4" />
            </Card>
          ))}
        </div>
      )}
      {error && (
        <Alert variant="destructive" className="mb-6">
          Error loading sites: {(error as Error).message}
        </Alert>
      )}
{sites && (
  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
    {sites.map((site: any) => (
      <Link
        key={site.id}
        to={`/sites/${site.slug}`}
        className="block group"
        style={{ textDecoration: "none" }}
      >
        <Card className="p-6 transition-shadow group-hover:shadow-lg cursor-pointer">
          {/* Image or Placeholder */}
          
          {site.screenshot_thumb ? (
            <img
              src={site.screenshot_thumb}
              alt={site.name}
              className="w-full h-36 object-cover rounded-md mb-4 border border-gray-200"
            />
          ) : (
            <div className="w-full h-36 flex items-center justify-center bg-gray-100 text-gray-400 rounded-md mb-4 border border-dashed border-gray-200">
              <span className="text-sm">No image</span>
            </div>
          )}
          <div className="text-lg font-semibold">{site.name}</div>
          <div className="text-muted-foreground mb-2">{site.subdomain}</div>
          {site.description && (
            <div className="text-sm text-gray-600 mb-2">{site.description}</div>
          )}
          <div>
            <span className="text-xs text-gray-400">ID: {site.id}</span>
          </div>

          
          {/*<div className="mt-4 flex gap-2">
            <Link to={`/sites/${site.slug}/themes`} className="w-full">
              <Button variant="outline" className="w-full">Themes</Button>
            </Link>
            <Link to={`/sites/${site.slug}/deployments`} className="w-full">
              <Button variant="outline" className="w-full">Deployments</Button>
            </Link>
            <Link to={`/sites/${site.slug}/collaborators`} className="w-full">
              <Button variant="outline" className="w-full">Collaborators</Button>
            </Link>
          </div>*/}
        </Card>
      </Link>
    ))}
  </div>
)}
    </div>
  );
};

export default SitesIndex;
